/**
 * AIRA Voice Test Page - Phase 3.2 + 3.4
 * 
 * Dedicated testing page for voice functionality.
 * Tests the complete browser → WebSocket → Voice Gateway → SharedBrain flow.
 * 
 * Phase 3.4 Features:
 * - Production VAD (Voice Activity Detection)
 * - Natural turn detection
 * - Enhanced barge-in support
 * - WebSocket reconnection
 * - Provider timeout handling
 * 
 * Uses Deepgram STT and OpenAI TTS (real providers).
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Mic, Volume2, Settings, Info, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AIRAVoiceOrb } from '../components/AIRAVoiceOrb';
import { useVoiceClient, VOICE_STATES } from '../hooks/useVoiceClient';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VoiceTestPage() {
  const navigate = useNavigate();
  
  // Voice client hook
  const {
    isConnected,
    sessionId,
    connect,
    disconnect,
    voiceState,
    isRecording,
    startRecording,
    stopRecording,
    micPermission,
    requestMicPermission,
    isSpeaking,
    interrupt,
    partialTranscript,
    finalTranscript,
    providerInfo,
    fetchStatus,
  } = useVoiceClient({
    autoConnect: false,
    language: 'en',
    voiceId: 'nova',
    onStateChange: (state) => {
      addLog(`State: ${state}`);
    },
    onTranscript: (transcript, isFinal) => {
      addLog(`Transcript ${isFinal ? '(FINAL)' : '(partial)'}: ${transcript}`);
    },
    onError: (code, message) => {
      addLog(`ERROR [${code}]: ${message}`, 'error');
      toast.error(message);
    },
    onAudioReceived: (audioData, sequence) => {
      addLog(`Audio chunk received: #${sequence}, ${audioData.length} bytes`);
    },
  });

  // Local state
  const [serviceStatus, setServiceStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);

  // Add log entry
  const addLog = (message, type = 'info') => {
    setLogs(prev => [{
      timestamp: new Date().toISOString(),
      message,
      type,
    }, ...prev].slice(0, 50));
  };

  // Fetch service status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/aira-voice/status`);
        const data = await response.json();
        setServiceStatus(data);
        addLog('Voice service status fetched');
      } catch (err) {
        addLog(`Failed to fetch status: ${err.message}`, 'error');
      }
    };
    checkStatus();
  }, []);

  // Add to conversation history
  useEffect(() => {
    if (finalTranscript) {
      setConversationHistory(prev => [...prev, {
        role: 'user',
        text: finalTranscript,
        timestamp: Date.now(),
      }]);
    }
  }, [finalTranscript]);

  // Handle orb click
  const handleOrbClick = async () => {
    if (voiceState === VOICE_STATES.IDLE || voiceState === VOICE_STATES.DISCONNECTED) {
      addLog('Connecting to voice service...');
      await connect();
      return;
    }

    if (voiceState === VOICE_STATES.CONNECTED || voiceState === VOICE_STATES.SUCCESS) {
      addLog('Starting recording...');
      const started = await startRecording();
      if (!started) {
        addLog('Failed to start recording', 'error');
        toast.error('Could not access microphone');
      }
      return;
    }

    if (voiceState === VOICE_STATES.LISTENING) {
      addLog('Stopping recording...');
      stopRecording();
      return;
    }

    if (voiceState === VOICE_STATES.SPEAKING) {
      addLog('Interrupting AIRA...');
      interrupt('user_tap');
      return;
    }
  };

  // Test microphone permission
  const handleTestMic = async () => {
    addLog('Testing microphone permission...');
    const granted = await requestMicPermission();
    if (granted) {
      addLog('Microphone permission granted');
      toast.success('Microphone access granted');
    } else {
      addLog('Microphone permission denied', 'error');
      toast.error('Microphone access denied');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0612] via-[#12101a] to-[#1a1528] text-white p-4 md:p-8" data-testid="voice-test-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-[#ff2d92] to-[#b347d9] bg-clip-text text-transparent">
              AIRA Voice Test
            </h1>
            <p className="text-gray-500 text-sm">Phase 3.2 Voice Integration Testing</p>
            <p className="text-emerald-400 text-xs mt-1">+ Phase 3.4 Hardening: VAD, Turn Detection, Barge-in, Reconnect</p>
          </div>
        </div>

        {/* Service Status */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Service Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-gray-500">Connection</p>
                <p className="text-sm font-medium flex items-center gap-1">
                  {isConnected ? (
                    <><CheckCircle className="w-4 h-4 text-green-500" /> Connected</>
                  ) : (
                    <><XCircle className="w-4 h-4 text-gray-500" /> Disconnected</>
                  )}
                </p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-gray-500">STT Provider</p>
                <p className="text-sm font-medium flex items-center gap-1">
                  {serviceStatus?.stt?.provider || 'unknown'}
                  {serviceStatus?.stt?.provider === 'mock' && (
                    <span className="px-1 py-0.5 bg-yellow-500/20 text-yellow-500 text-[10px] rounded">MOCK</span>
                  )}
                </p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-gray-500">TTS Provider</p>
                <p className="text-sm font-medium flex items-center gap-1">
                  {serviceStatus?.tts?.provider || 'unknown'}
                  {serviceStatus?.tts?.provider === 'mock' && (
                    <span className="px-1 py-0.5 bg-yellow-500/20 text-yellow-500 text-[10px] rounded">MOCK</span>
                  )}
                </p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-gray-500">Microphone</p>
                <p className="text-sm font-medium capitalize">{micPermission || 'unknown'}</p>
              </div>
            </div>

            {/* Mock Warning */}
            {serviceStatus?.stt?.provider === 'mock' && (
              <div className="mt-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-500">
                  <p className="font-medium">Using MOCK Providers</p>
                  <p className="text-xs opacity-80 mt-1">
                    Deepgram/ElevenLabs credentials are not configured. Voice will work with simulated transcripts and responses.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Voice Orb */}
        <Card className="bg-white/5 border-white/10">
          <CardContent className="py-12">
            <div className="flex flex-col items-center">
              <AIRAVoiceOrb
                state={voiceState}
                size="xl"
                onClick={handleOrbClick}
                showLabel={true}
                transcript={partialTranscript || finalTranscript}
              />
              
              <p className="mt-8 text-gray-500 text-sm text-center max-w-xs">
                {voiceState === VOICE_STATES.IDLE && 'Tap the orb to connect'}
                {voiceState === VOICE_STATES.CONNECTED && 'Tap to start speaking'}
                {voiceState === VOICE_STATES.LISTENING && 'Speak now... Tap again to stop'}
                {voiceState === VOICE_STATES.SPEAKING && 'AIRA is speaking... Tap to interrupt'}
                {voiceState === VOICE_STATES.THINKING && 'Processing your request...'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-400">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleTestMic}
                className="bg-white/5 border-white/10 hover:bg-white/10"
                data-testid="test-mic-btn"
              >
                <Mic className="w-4 h-4 mr-1" />
                Test Microphone
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => isConnected ? disconnect() : connect()}
                className="bg-white/5 border-white/10 hover:bg-white/10"
                data-testid="connect-btn"
              >
                {isConnected ? 'Disconnect' : 'Connect'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchStatus().then(data => setServiceStatus(data))}
                className="bg-white/5 border-white/10 hover:bg-white/10"
                data-testid="refresh-status-btn"
              >
                Refresh Status
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLogs([])}
                className="bg-white/5 border-white/10 hover:bg-white/10"
              >
                Clear Logs
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Conversation History */}
        {conversationHistory.length > 0 && (
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Conversation History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {conversationHistory.map((item, idx) => (
                  <div
                    key={idx}
                    className={`p-2 rounded-lg ${
                      item.role === 'user'
                        ? 'bg-[#ff2d92]/10 border border-[#ff2d92]/30 ml-8'
                        : 'bg-white/5 border border-white/10 mr-8'
                    }`}
                  >
                    <p className="text-sm">{item.text}</p>
                    <p className="text-[10px] text-gray-500 mt-1">
                      {new Date(item.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Debug Logs */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
              <Info className="w-4 h-4" />
              Debug Logs ({logs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48 overflow-y-auto font-mono text-xs bg-black/30 rounded-lg p-3 space-y-1">
              {logs.length === 0 ? (
                <p className="text-gray-600">No logs yet...</p>
              ) : (
                logs.map((log, idx) => (
                  <div
                    key={idx}
                    className={`${
                      log.type === 'error' ? 'text-red-400' :
                      log.type === 'warn' ? 'text-yellow-400' :
                      'text-gray-400'
                    }`}
                  >
                    <span className="text-gray-600">{new Date(log.timestamp).toLocaleTimeString()}</span>
                    {' '}
                    {log.message}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Session Info */}
        {sessionId && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-3">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Session ID: <span className="font-mono">{sessionId.slice(0, 16)}...</span></span>
                <span>Voice State: <span className="font-medium text-white">{voiceState}</span></span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
